<template>
    <div>
        <b-table :items="tableItems" :fields="tableFields" class="mb-0" small bordered>
            <template #cell(label)="{ item, index }">
                <b-input v-model="editingRowData.label" v-if="editingRowIndex === index" autofocus
                    placeholder="Label (e.g. Lilly Support)" />
                <template v-else>{{ item.value.label }}</template>
            </template>
            <template #cell(number)="{ item, index }">
                <template v-if="editingRowIndex === index">
                    <b-input v-model="editingRowData.number"
                        :state="editingRowData.number?.trim() ? editingNumberValid : null"
                        placeholder="Phone number (e.g. +18475551234)" />
                    <b-form-invalid-feedback :state="editingRowData.number?.trim() ? editingNumberValid : null">
                        Enter a valid phone number in E.164 format (e.g. +18475551234).
                    </b-form-invalid-feedback>
                </template>
                <template v-else>{{ item.value.number }}</template>
            </template>
            <template #cell(actions)="{ item, index }">
                <div class="d-flex" style="gap: 6px;">
                    <template v-if="editingRowIndex !== index">
                        <b-button variant="link" @click.prevent="startTableEdit(item.value, index)" title="Edit">
                            <b-icon icon="pencil" variant="primary" />
                        </b-button>
                        <b-button variant="link" @click.prevent="removeTableItem(index)" title="Remove">
                            <b-icon icon="x-circle-fill" variant="danger" />
                        </b-button>
                    </template>
                    <template v-else>
                        <b-button variant="link" title="Save" @click.prevent="saveTableEdit(index)"
                            :disabled="!editingNumberValid">
                            <b-icon icon="save" variant="primary" />
                        </b-button>
                        <b-button variant="link" title="Cancel Edit" @click.prevent="cancelTableEdit">
                            <b-icon icon="x-lg" variant="" />
                        </b-button>
                    </template>
                </div>
            </template>
        </b-table>

        <b-button size="sm" variant="primary" @click="addTableItem">
            Add
        </b-button>
    </div>
</template>

<script lang="ts">
import { Vue, Prop, Component, Watch } from "vue-property-decorator";
import { QuickDialNumber } from "@/models/Configuration";

@Component({
    name: "QuickDialNumbersField",
})
export default class QuickDialNumbersField extends Vue {
    @Prop({ default: () => [] }) value!: QuickDialNumber[];

    editingRowData: QuickDialNumber = { label: "", number: "" };
    editingRowIndex: number = -1;
    localValue: QuickDialNumber[] = [];

    @Watch('value', { immediate: true, deep: true })
    onValueChange(newValue: QuickDialNumber[]) {
        if (this.editingRowIndex >= 0) return;
        this.localValue = [...(newValue || [])];
    }

    get tableItems() {
        return this.localValue.map((ir, ix) => ({ index: ix, value: ir }));
    }

    get tableFields() {
        return [
            { key: 'label', label: 'Label' },
            { key: 'number', label: 'Phone Number' },
            { key: 'actions', label: '', class: 'actions' },
        ];
    }

    startTableEdit(item: QuickDialNumber, index: number) {
        this.editingRowIndex = index;
        this.editingRowData = { ...item };
    }

    addTableItem() {
        if (this.editingRowIndex >= 0 && !this.saveTableEdit(this.editingRowIndex)) {
            return;
        }

        const cleanedArray = this.localValue.filter((a) => a.number.trim());
        cleanedArray.push({ label: "", number: "" });
        this.localValue = cleanedArray;
        this.startTableEdit({ label: "", number: "" }, cleanedArray.length - 1);
    }

    removeTableItem(index: number) {
        const array = [...this.localValue];
        array.splice(index, 1);
        this.localValue = array;
        this.$emit('input', [...this.localValue]);
    }

    cancelTableEdit() {
        const editingIndex = this.editingRowIndex;
        const editingValue = this.localValue[editingIndex]?.number;
        if (!editingValue) {
            this.removeTableItem(editingIndex);
        }
        this.editingRowIndex = -1;
        this.editingRowData = { label: "", number: "" };
    }

    normalizeToE164(raw: string): string | null {
        const trimmed = (raw || "").trim();
        if (!/^[+\d\s().-]*$/.test(trimmed)) return null;
        const digits = trimmed.replace(/\D/g, "");
        let normalized: string;
        if (trimmed.startsWith("+")) normalized = `+${digits}`;
        else if (digits.length === 10) normalized = `+1${digits}`;
        else if (digits.length === 11 && digits.startsWith("1")) normalized = `+${digits}`;
        else normalized = trimmed;
        return this.isValidE164(normalized) ? normalized : null;
    }

    isValidE164(number: string): boolean {
        return /^\+[1-9]\d{7,14}$/.test(number);
    }

    get editingNumberValid(): boolean {
        return this.normalizeToE164(this.editingRowData.number || "") !== null;
    }

    saveTableEdit(index: number): boolean {
        const trimmedNumber = this.editingRowData.number?.trim();
        if (!trimmedNumber) {
            const emptied = [...this.localValue];
            emptied.splice(index, 1);
            this.localValue = emptied;
            this.$emit('input', [...this.localValue]);
            this.editingRowIndex = -1;
            this.editingRowData = { label: "", number: "" };
            return true;
        }

        const normalized = this.normalizeToE164(trimmedNumber);
        if (!normalized) {
            return false;
        }

        const array = [...this.localValue];
        array.splice(index, 1, {
            label: this.editingRowData.label?.trim() || "",
            number: normalized,
        });
        this.localValue = array;
        this.$emit('input', [...this.localValue]);
        this.editingRowIndex = -1;
        this.editingRowData = { label: "", number: "" };
        return true;
    }
}
</script>

<style scoped lang="scss">
::v-deep(thead th),
::v-deep(td) {
    padding: 1px 7px !important;
}

::v-deep(td) {
    height: 38px !important;
}

table ::v-deep(.actions) {
    width: 90px;
}
</style>
